import { Component, ReactNode } from "react";
import { useRouteError } from "react-router";

import { Alert } from "@prequel/shared-components";

interface ErrorBoundaryProps {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex min-h-screen items-center justify-center p-8">
          <div className="max-w-md">
            <Alert>
              <div className="mb-2 font-semibold">Something went wrong</div>
              <div className="text-sm">{this.state.error?.message || "An unexpected error occurred"}</div>
            </Alert>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Router error element (functional component for use in errorElement prop)
export const RouterErrorBoundary = () => {
  const error = useRouteError();
  const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";

  return (
    <div className="flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md">
        <Alert>
          <div className="mb-2 font-semibold">Something went wrong</div>
          <div className="text-sm">{errorMessage}</div>
        </Alert>
      </div>
    </div>
  );
};

export default ErrorBoundary;
